import React, { useState, useEffect } from 'react';
import { Card, Stack, Button, Typography, Box } from '@mui/material';
import PropTypes from 'prop-types';
import {
  LISTING_COLUMNS,
  LISTING_ACTIONS,
} from '../../config/module-configs/packages.config';
import ListData from '../table-elements/list-data.component';
import Iconify from '../common/iconify.component';
import {
  createNewDefaultPackages,
  deleteDataById,
  getListData,
} from '../../services/packages.service';
import { ACTIONS } from '../../config/const.config';
import SnackbarInfo from '../common/snackbar-info.component';
import ConfirmPopup from '../common/confirm-popup.component';
import AddEditContentAppPackage from './add-edit-app-content-package.component';
import ViewAppContantPackage from './view-app-content-package.component';
import Loading from '../table-elements/loading.component';
import useFilteredActions from '../../hooks/filtered-action.hook';
import useAbortableRequest from '../../hooks/abortable-request.hook';

const ListAppContentPackages = ({ channelId, viewMode }) => {
  const columns = LISTING_COLUMNS;
  // eslint-disable-next-line no-nested-ternary
  const actions = !viewMode
    ? channelId === 0
      ? useFilteredActions('global_assets', LISTING_ACTIONS)
      : LISTING_ACTIONS
    : [];

  const { getSignal, isAbortError } = useAbortableRequest();

  const [options, setOptions] = useState({
    page: 0,
    rowsPerPage: 100,
    totalRows: 0,
    rows: [],
    reloadCounter: 0,
    sortBy: '',
    sortOrder: '',
    error: false,
    loading: true,
    appliedFilters: {
      search: '',
    },
    filtersCounter: 0,
    isWebIapAssigned: false,
  });
  const [doAction, setDoAction] = useState({
    data: null,
    action: null,
  });
  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });

  const handlePageChange = (v) => {
    setOptions({
      ...options,
      page: v,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };
  const handleRowsPerPageChange = (v) => {
    setOptions({
      ...options,
      rowsPerPage: v,
      page: 0,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };
  const handleSortingChange = (sortBy, sortOrder) => {
    setOptions({
      ...options,
      sortBy,
      sortOrder,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };
  const handleRefreshData = () => {
    setOptions({
      ...options,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };

  const handleActionSelect = (selectedAction, selectedData) => {
    setDoAction({
      ...doAction,
      data: selectedData || null,
      action: selectedAction || null,
    });
  };
  const handleActionCancel = () => {
    setDoAction({
      ...doAction,
      data: null,
      action: null,
    });
  };

  const handleActionSuccess = (message = '') => {
    if (message) {
      setSnackbarInfo({
        ...snackbarInfo,
        show: true,
        type: 'success',
        message,
      });
    }

    handleActionCancel();
    handleRefreshData();
  };

  const handleDeleteData = () => {
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });

    deleteDataById(doAction.data.id)
      .then(() => {
        handleActionSuccess('Package deleted successfully.');
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        handleActionCancel();
      });
  };

  const handleCreateDefaulPackages = () => {
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });
    setOptions({
      ...options,
      loading: true,
    });

    createNewDefaultPackages(channelId)
      .then(() => {
        handleActionSuccess('Package Created successfully.');
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        handleRefreshData();
      });
  };

  useEffect(() => {
    const signal = getSignal();
    const params = [];
    params.push(`page=${options.page + 1}`);
    params.push(`perPage=${options.rowsPerPage}`);
    if (options.sortBy && options.sortOrder) {
      params.push(`sortBy=${options.sortBy}`);
      params.push(`sortOrder=${options.sortOrder}`);
    }
    const filters = options.appliedFilters;
    if (filters.search) {
      params.push(`q=${encodeURIComponent(filters.search)}`);
    }

    const paramsQuery = params.length > 0 ? `?${params.join('&')}` : '';
    getListData(channelId, paramsQuery, signal)
      .then((res) => {
        if (!signal.aborted) {
          setOptions({
            ...options,
            loading: false,
            totalRows: res?.data?.totalRows || 0,
            rows: res?.data?.rows || [],
            isWebIapAssigned: res?.data?.isWebIapAssigned || false,
            error: false,
          });
        }
      })
      .catch((err) => {
        if (isAbortError(err)) return;
        setOptions({
          ...options,
          loading: false,
          page: 0,
          totalRows: 0,
          rows: [],
          error: true,
        });
      });
  }, [options.reloadCounter]);

  return (
    <>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ px: 0, py: 2 }}
      >
        <Box color="red" fontSize="small" ml={1}>
          Subscription plans in this section only appear on the channel website
          and will not be used for other platforms (e.g. Roku, Fire TV, etc).
        </Box>
        <div>
          <Button
            color="inherit"
            variant="contained"
            startIcon={<Iconify icon="ic:twotone-refresh" />}
            sx={{ marginRight: channelId ? 1 : 0 }}
            onClick={handleRefreshData}
            disabled={options.loading}
          >
            Refresh
          </Button>
        </div>
      </Stack>

      <Card>
        {options.loading && <Loading />}
        {options.rows?.length > 0 && (
          <ListData
            columns={columns}
            rows={options.rows}
            page={options.page}
            rowsPerPage={options.rowsPerPage}
            totalRows={options.totalRows}
            loading={options.loading}
            actions={actions}
            error={options.error}
            sortBy={options.sortBy}
            sortOrder={options.sortOrder}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            onSortChange={handleSortingChange}
            onAction={handleActionSelect}
          />
        )}
        {!options.loading &&
          options.rows?.length === 0 &&
          !options.isWebIapAssigned && (
            <Typography
              variant="body2"
              sx={{ my: 20, textAlign: 'center', px: 5 }}
            >
              It looks like you haven&apos;t set any payment validation source
              for this channel on web platform. Please go to{' '}
              <strong>Edit</strong> channel &gt;{' '}
              <strong>Monetization Settings</strong> section to select payment
              validation source on web platform.
              <br />
              Once it is done, you can return here and create packages for this
              channel.
            </Typography>
          )}
        {!options.loading &&
          options.rows?.length === 0 &&
          options.isWebIapAssigned && (
            <Box sx={{ my: 20, textAlign: 'center', px: 5 }}>
              {!viewMode ? (
                <Button
                  color="inherit"
                  variant="contained"
                  onClick={handleCreateDefaulPackages}
                  disabled={options.loading}
                >
                  Setup Packages for This Channel
                </Button>
              ) : (
                <Typography variant="body2">
                  No packages found for this channel.
                </Typography>
              )}
            </Box>
          )}
      </Card>

      {doAction.action === ACTIONS.CREATE.value && (
        <AddEditContentAppPackage
          title="Create New Package"
          dataId={0}
          onCancel={handleActionCancel}
          onSuccess={handleActionSuccess}
          channelId={channelId}
        />
      )}

      {doAction.action === ACTIONS.EDIT.value && (
        <AddEditContentAppPackage
          title={`Edit Package Details - ${doAction.data.name}`}
          dataId={doAction.data.id}
          onCancel={handleActionCancel}
          onSuccess={handleActionSuccess}
          channelId={channelId}
        />
      )}

      {doAction.action === ACTIONS.VIEW.value && (
        <ViewAppContantPackage
          title={`Package Details - ${doAction.data.name}`}
          dataId={doAction.data.id}
          onCancel={handleActionCancel}
        />
      )}

      {doAction.action === ACTIONS.DELETE.value && (
        <ConfirmPopup
          title="Delete Package"
          message={`Do you want to delete ${doAction.data.name} package? You can not undo this action!`}
          onCancel={handleActionCancel}
          onSuccess={handleDeleteData}
          successButtonText={!channelId ? 'Delete' : 'Yes'}
          cancelButtonText={!channelId ? 'Cancel' : 'No'}
          showConfirmationInput={!channelId}
          confirmationInputText="delete"
        />
      )}

      {snackbarInfo.show && (
        <SnackbarInfo type={snackbarInfo.type} message={snackbarInfo.message} />
      )}
    </>
  );
};

ListAppContentPackages.propTypes = {
  channelId: PropTypes.number.isRequired,
  viewMode: PropTypes.bool,
};
ListAppContentPackages.defaultProps = {
  viewMode: false,
};

export default ListAppContentPackages;
